import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container,Box, Typography, CircularProgress, Alert } from '@mui/material';
import '../App.css';

// Create audio element with public URL
const clickAudio = new Audio('/sounds/click.mp3');

// Preload the audio file
clickAudio.load();

const HomePage = () => {
  const [quizzes, setQuizzes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadQuizzes = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch('/quiz-data/quizzes.json');
        if (!response.ok) {
          throw new Error('Failed to load quizzes');
        }
        const data = await response.json();
        // Handle both array and object with quizzes property
        const quizzesList = Array.isArray(data) ? data : (data.quizzes || []);
        setQuizzes(quizzesList);
      } catch (err) {
        console.error('Failed to load quizzes:', err);
        setError('Failed to load quizzes. Please try again later.');
        setQuizzes([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadQuizzes();
  }, []);

  const handleStartQuiz = (quizId) => {
    clickAudio.play();
    navigate(`/quiz/${quizId}`);
  };


  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      py: 2,
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)'
    }}>
      <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
        <Box textAlign="center" mb={2}>
          <img 
            className="app-logo"
            src="/images/app-logo.png" 
            alt="Quiz App Logo"
            style={{ maxWidth: '240px' }}
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = '/images/logo.png';
            }}
          />
          <Typography variant="h6" sx={{ mt: 1, color: '#4a6fa5' }}>
            Test your knowledge with our interactive quizzes
          </Typography>
        </Box>

        {quizzes.length > 0 ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)'
            },
            gap: '16px',
            width: '100%',
            justifyContent: 'center',
            justifyItems: 'center'
          }}>
            {quizzes.map((quiz) => (
              <Box key={quiz.id} sx={{
                width: '100%',
                maxWidth: '300px',
                display: 'flex',
                justifyContent: 'center'
              }}>
                <div className="quiz-card-wrapper">
                  <div className="quiz-card">
                    <div className="quiz-image">
                      {quiz.image ? (
                        <img 
                          src={quiz.image} 
                          alt={quiz.title} 
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/quiz-placeholder.png';
                          }} 
                        />
                      ) : (
                        <div className="quiz-image-placeholder">
                          <span>{quiz.title.charAt(0).toUpperCase()}</span>
                        </div>
                      )}
                    </div>
                    <div className="quiz-info">
                      <h2>{quiz.title}</h2>
                      <p>{quiz.description || 'Test your knowledge with this quiz'}</p>
                      <button 
                        className="start-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStartQuiz(quiz.id);
                        }}
                        disabled={quiz.comingSoon}
                      >
                        {quiz.comingSoon ? 'Coming Soon' : 'Start Quiz'}
                      </button>
                    </div>
                  </div>
                </div>
              </Box>
            ))}
          </Box>
        ) : (
          <Box textAlign="center" py={8}>
            <Typography variant="h6" color="textSecondary">
              No quizzes available at the moment. Please check back later.
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default HomePage;
